import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useWallet } from './WalletContext';

const NotificationsContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { publicKey } = useWallet();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Map notification types to UI types
  const getNotificationType = useCallback((type) => {
    const typeMap = {
      'piece_sold': 'success',
      'piece_purchased': 'success',
      'offer_received': 'info',
      'offer_accepted': 'success',
      'offer_declined': 'alert',
      'offer_expired': 'alert',
      'trade_received': 'info',
      'trade_accepted': 'success',
      'trade_declined': 'alert',
      'price_alert': 'alert',
      'collection_featured': 'success',
      'verification_approved': 'success',
      'verification_rejected': 'alert',
      'piece_listed': 'info',
      'system': 'info'
    };
    return typeMap[type] || 'info';
  }, []);

  // Fetch notifications for the connected wallet
  const fetchNotifications = useCallback(async () => {
    if (!publicKey) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setIsLoading(true);
      const walletAddress = publicKey.toString();

      // Fetch notifications from active_notifications view
      console.log('📥 Fetching notifications for wallet:', walletAddress);
      const { data, error } = await supabase
        .from('active_notifications')
        .select('*')
        .eq('wallet', walletAddress)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('❌ Error fetching notifications:', error);
        // Clear notifications on error
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      console.log('📥 Fetched', data?.length || 0, 'notifications from database');

      // Always set notifications based on data (even if empty)
      if (data) {
        // Transform data to match our component format
        const transformedNotifications = data.map(notification => ({
          id: notification.id,
          type: getNotificationType(notification.type),
          title: notification.title,
          message: notification.message,
          time: notification.time_ago,
          unread: !notification.is_read,
          // Additional data
          pieceId: notification.piece_id,
          pieceName: notification.piece_name,
          pieceImageUrl: notification.piece_image_url,
          mintAddress: notification.mint_address,
          amountSol: notification.amount_sol,
          transactionSignature: notification.transaction_signature,
          offerId: notification.offer_id,
          tradeId: notification.trade_id,
          metadata: notification.metadata
        }));

        setNotifications(transformedNotifications);
        
        // Count unread
        const unread = transformedNotifications.filter(n => n.unread).length;
        setUnreadCount(unread);
      } else {
        // If no data returned, clear notifications
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Clear notifications on exception
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, getNotificationType]);

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const { error } = await supabase.rpc('mark_notification_read', {
        notification_id: notificationId
      });

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, unread: false } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!publicKey) return;

    try {
      const walletAddress = publicKey.toString();
      const { error } = await supabase.rpc('mark_all_notifications_read', {
        user_wallet: walletAddress
      });

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, unread: false }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [publicKey]);

  // Delete (dismiss) a notification
  const deleteNotification = useCallback(async (notificationId) => {
    // Get notification before removing
    const deletedNotification = notifications.find(n => n.id === notificationId);
    
    // Immediately update local state
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    
    // Update unread count if the deleted notification was unread
    if (deletedNotification?.unread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    try {
      const { error } = await supabase.rpc('delete_notification', {
        notification_id: notificationId
      });

      if (error) {
        console.error('Error deleting notification from database:', error);
        // Keep local state cleared even if database call fails
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Keep local state cleared even if exception occurs
    }
  }, [notifications]);

  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    if (!publicKey) return;

    console.log('🗑️ Deleting all notifications...');
    
    // Immediately clear local state
    setNotifications([]);
    setUnreadCount(0);

    try {
      const walletAddress = publicKey.toString();
      
      console.log('🗑️ Calling delete_all_notifications RPC for wallet:', walletAddress);
      const { data, error } = await supabase.rpc('delete_all_notifications', {
        user_wallet: walletAddress
      });

      if (error) {
        console.error('❌ Error deleting all notifications:', error);
        console.error('Error details:', error.message, error.details, error.hint);
      } else {
        console.log('✅ Successfully deleted', data, 'notifications');
      }
      
      // Force a fresh fetch after deletion
      console.log('🔄 Force fetching after delete...');
      await fetchNotifications();
      
    } catch (error) {
      console.error('❌ Exception deleting all notifications:', error);
    }
  }, [publicKey, fetchNotifications]);

  // Create a test notification (for development)
  const createTestNotification = useCallback(async () => {
    if (!publicKey) return;

    try {
      const walletAddress = publicKey.toString();
      const { error } = await supabase
        .from('user_notifications')
        .insert({
          wallet: walletAddress,
          type: 'system',
          title: 'Test Notification',
          message: 'This is a test notification',
          metadata: { test: true }
        });

      if (error) throw error;

      // Refresh notifications
      await fetchNotifications();
    } catch (error) {
      console.error('Error creating test notification:', error);
    }
  }, [publicKey, fetchNotifications]);

  // Set up real-time subscription
  useEffect(() => {
    if (!publicKey) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Initial fetch
    fetchNotifications();

    // Set up real-time subscription for new notifications
    const walletAddress = publicKey.toString();
    const subscription = supabase
      .channel('user_notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_notifications',
          filter: `wallet=eq.${walletAddress}`
        },
        (payload) => {
          console.log('Notification change received:', payload);
          // Refresh notifications when changes occur
          fetchNotifications();
        }
      )
      .subscribe();

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [publicKey, fetchNotifications]);

  // Refresh notifications periodically (every 30 seconds)
  useEffect(() => {
    if (!publicKey) return;

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [publicKey, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    fetchNotifications,
    createTestNotification
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsContext;

